
import { useState, useEffect, useRef, type ReactNode } from "react";
import { Music, Globe, DollarSign, BarChart4, Headphones, Award } from "lucide-react";

type FeatureCardProps = {
  className?: string;
  icon: ReactNode;
  title: string;
  description: string;
  index?: number;
};

const FeatureCard = ({ className, icon, title, description, index = 0 }: FeatureCardProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setTimeout(() => {
            setIsVisible(true);
          }, index * 100);
        }
      },
      { threshold: 0.1 }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
    };
  }, [index]);

  return (
    <div
      ref={cardRef}
      className={`bg-ophira-darkgray/30 border border-ophira-darkgray/70 p-6 md:p-8 flex flex-col items-start transition-all duration-700 rounded-[0.65rem] ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-20"
      } hover:bg-ophira-darkgray/50 group relative ${className ?? ""}`}
    >
      <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-ophira-yellow/10 flex items-center justify-center mb-4 md:mb-6 group-hover:bg-ophira-yellow/20 transition-colors duration-300">
        {icon}
      </div>
      <h3 className="text-lg md:text-xl font-bold mb-2 md:mb-3 text-white group-hover:text-ophira-yellow transition-colors duration-300">{title}</h3>
      <p className="text-sm md:text-base text-white/65">{description}</p>
      
      {/* Hover animation effect */}
      <div className="absolute bottom-0 left-0 w-0 h-1 bg-ophira-yellow group-hover:w-full transition-all duration-500"></div>
    </div>
  );
};

const FeaturesSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section ref={sectionRef} className="py-16 md:py-20 bg-black relative overflow-hidden">
      <div className="container mx-auto px-4">
        <div className={`text-center max-w-3xl mx-auto mb-12 md:mb-16 transition-all duration-700 ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
        }`}>
          <div className="ophira-section-label justify-center">FEATURES</div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 md:mb-6 relative inline-block text-white">
            Why Choose <span className="text-ophira-yellow relative z-10">YouAux</span>
          </h2>
          <p className="text-white/65 text-base md:text-lg">
            Our platform offers everything independent artists need to succeed in today's music industry.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
          <FeatureCard 
            className="rounded-[0.65rem]"
            icon={<Globe className="text-ophira-yellow h-5 w-5 md:h-7 md:w-7" />}
            title="Worldwide Distribution"
            description="Distribute your music to over 150 streaming platforms including Spotify, Apple Music, Amazon, and more."
            index={0}
          />
          <FeatureCard
            icon={<DollarSign className="text-ophira-yellow h-5 w-5 md:h-7 md:w-7" />}
            title="Keep 100% Ownership"
            description="Unlike other platforms, we let you keep 100% of your music ownership and royalties with our subscription model."
            index={1}
          />
          <FeatureCard
            icon={<Music className="text-ophira-yellow h-5 w-5 md:h-7 md:w-7" />}
            title="Pre-Release Tools"
            description="Schedule releases, create pre-save campaigns, and build anticipation for your new music."
            index={2}
          />
          <FeatureCard
            icon={<BarChart4 className="text-ophira-yellow h-5 w-5 md:h-7 md:w-7" />}
            title="Advanced Analytics"
            description="Track your performance across all platforms with detailed insights and audience demographics."
            index={3}
          />
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
